import React, { useState, useEffect } from "react";
import { Plus, Pencil } from "lucide-react";
import { getMarketingCosts, createMarketingCost, updateMarketingCost } from "../../../services/AdminPortal/financeAPI";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const MarketingCosts = ({ onDataChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [marketingType, setMarketingType] = useState("");
  const [amount, setAmount] = useState("");
  const [marketing, setMarketing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMarketingCosts();
  }, []);

  const fetchMarketingCosts = async () => {
    try {
      const data = await getMarketingCosts();
      setMarketing(data);
    } catch (error) {
      console.error("Error fetching marketing costs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!marketingType || !amount) return;
    
    try {
      const payload = {
        type: marketingType,
        amount: parseFloat(amount),
      };

      if (editingItem) {
        await updateMarketingCost(editingItem._id, payload);
      } else {
        await createMarketingCost(payload);
      }
      
      setShowModal(false);
      resetForm();
      await fetchMarketingCosts();
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Error saving marketing cost:", error);
    }
  };

  const resetForm = () => {
    setMarketingType("");
    setAmount("");
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setMarketingType(item.type);
    setAmount(item.amount.toString());
    setShowModal(true);
  };

  // Generate stable colors for pie chart based on marketing type
  const getColorForType = (type) => {
const colorMap = {
  'Digital Marketing': '#FFCA20',   // yellow
  'Offline Marketing': '#4ADE80',   // green
  'Social Media': '#FF6B6B',        // red
  'Content Marketing': '#60A5FA',   // blue
  'Email Marketing': '#A78BFA',     // purple
  'Other': '#F59E0B',               // orange
  'Cold callers': '#EC4899',        // pink (distinct from red/purple)
  'Campaigns': '#10B981',           // teal (distinct from green)
};

    return colorMap[type] || '#9CA3AF';
  };

  const chartData = {
    labels: marketing.map(item => item.type),
    datasets: [{
      data: marketing.map(item => item.amount),
      backgroundColor: marketing.map(item => getColorForType(item.type)),
      borderWidth: 0,
      hoverOffset: 8,
    }]
  };

  const chartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percent = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ₹${context.raw.toLocaleString('en-IN')} (${percent}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  const total = marketing.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="bg-[#131313] h-[400px] border border-[#333] p-4 rounded-lg w-full flex flex-col relative overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-sm font-semibold">MARKETING COSTS</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-semibold px-2 py-1 rounded"
        >
          + Add
        </button>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-4">Loading...</div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          {marketing.length > 0 ? (
            <>
              <div className="h-40 mb-4">
                <Pie data={chartData} options={chartOptions} />
              </div>
              {/* marketing list */}
              <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent pr-2">
                <div className="space-y-2">
                  {marketing.map((item, idx) => (
                    <div key={item._id || idx} className="flex items-center justify-between text-xs p-2 bg-[#1a1a1a] rounded min-w-0">
                      <div className="flex items-center min-w-0 flex-1">
                        <div
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor: getColorForType(item.type) }}
                        />
                        <span className="text-gray-300 truncate">{item.type}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex flex-col items-end">
                          <span className="text-white font-medium whitespace-nowrap">₹{item.amount.toLocaleString("en-IN")}</span>
                          <span className="text-gray-400 text-xs">
                            {total ? ((item.amount / total) * 100).toFixed(1) : 0}%
                          </span>
                        </div>
                        <Pencil 
                          size={12} 
                          className="text-yellow-300 cursor-pointer flex-shrink-0" 
                          onClick={() => handleEdit(item)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-400 py-8">
              No marketing costs found
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-md shadow-lg border border-[#333]">
            <h3 className="text-white text-base font-semibold mb-4">
              {editingItem ? "Edit Marketing Cost" : "Add Marketing Cost"}
            </h3>

            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-1">Marketing Type</label>
              <select
                value={marketingType}
                onChange={(e) => setMarketingType(e.target.value)}
                className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] focus:outline-none"
              >
                <option value="">Select marketing type</option>
                <option value="Digital Marketing">Digital Marketing</option>
                <option value="Offline Marketing">Offline Marketing</option>
                <option value="Social Media">Social Media</option>
                <option value="Content Marketing">Content Marketing</option>
                <option value="Email Marketing">Email Marketing</option>
                <option value="Cold callers">Cold callers</option>
                <option value="Campaigns">Campaigns</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="mb-5">
              <label className="text-gray-400 text-sm block mb-1">Amount Spent</label>
              <input
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] placeholder-gray-500 focus:outline-none"
              />
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="bg-[#2b2b2b] text-white px-4 py-2 rounded text-sm border border-[#444]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!marketingType || !amount}
                className="bg-yellow-400 text-black px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
              >
                {editingItem ? "Update" : "Confirm"} ✓
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingCosts;